import React, { useState, useRef } from 'react';
import { Eye, EyeOff, Shield } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * SecureMessage Component
 * Messages are blurred by default and only revealed on hover
 * This prevents automated screen scraping and surveillance
 */
export const SecureMessage = ({ 
  children, 
  messageId, 
  isSelf, 
  isWindowFocused = true,
  alwaysVisible = false,
  className 
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [hasBeenRevealed, setHasBeenRevealed] = useState(false);
  const messageRef = useRef(null);

  const handleMouseEnter = () => {
    setIsHovered(true);
    setHasBeenRevealed(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  // Determine if content should be visible. `alwaysVisible` bypasses the
  // hover-to-reveal overlay entirely (used while the bubble is in inline
  // edit mode — otherwise the overlay would intercept clicks on the
  // Save/Cancel buttons and the user can't edit what they can't see).
  const isVisible = alwaysVisible || (isHovered && isWindowFocused);
  const showBlur = !isVisible;

  return (
    <div
      ref={messageRef}
      className={cn(
        'relative group cursor-pointer transition-all duration-200',
        className
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-testid={`secure-message-${messageId}`}
    >
      {/* Message content with conditional blur */}
      <div
        className={cn(
          'transition-all duration-300 select-none',
          showBlur && 'blur-md'
        )}
        style={{
          // Additional CSS to prevent easy copying
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none',
          userSelect: 'none',
        }}
      >
        {children}
      </div>

      {/* Hover overlay when blurred */}
      {showBlur && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 rounded-2xl backdrop-blur-sm">
          <div className="flex items-center gap-2 text-xs text-slate-300">
            <Eye className="w-4 h-4" />
            <span>Hover to reveal</span>
          </div>
        </div>
      )}

      {/* Security indicator when revealed */}
      {isVisible && (
        <div className="absolute -top-1 -right-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="flex items-center gap-1 bg-emerald-500/20 text-emerald-400 text-[10px] px-1.5 py-0.5 rounded-full border border-emerald-500/30">
            <Shield className="w-2.5 h-2.5" />
            <span>Secure</span>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * SecurityOverlay Component
 * Shows when window loses focus to prevent screen capture
 */
export const SecurityOverlay = ({ isVisible, username }) => {
  if (!isVisible) return null;

  return (
    <div 
      className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-xl flex items-center justify-center"
      style={{ backgroundColor: '#05050A' }}
    >
      <div className="text-center p-8 max-w-md">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-6">
          <EyeOff className="w-8 h-8 text-primary" />
        </div>
        <h2 className="text-xl font-semibold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Content Protected
        </h2>
        <p className="text-slate-400 mb-4">
          Messages are hidden while the window is not in focus to prevent unauthorized viewing.
        </p>
        <p className="text-sm text-slate-500">
          Click anywhere to return to your conversation
        </p>
      </div>
    </div>
  );
};

/**
 * DevToolsWarning Component
 * Shows warning when developer tools are detected
 */
export const DevToolsWarning = ({ isVisible }) => {
  if (!isVisible) return null;

  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[90] animate-fadeIn">
      <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 text-amber-400 px-4 py-2 rounded-lg backdrop-blur-sm">
        <Shield className="w-4 h-4" />
        <span className="text-sm font-medium">Developer tools detected - some features may be restricted</span>
      </div>
    </div>
  );
};

/**
 * Watermark Component
 * Subtle watermark on messages to deter screenshot sharing
 */
export const MessageWatermark = ({ watermarkText }) => {
  return (
    <div 
      className="absolute inset-0 pointer-events-none overflow-hidden opacity-[0.03] select-none"
      style={{
        backgroundImage: `repeating-linear-gradient(
          -45deg,
          transparent,
          transparent 100px,
          currentColor 100px,
          currentColor 101px
        )`,
      }}
    >
      <div className="absolute inset-0 flex items-center justify-center">
        <span 
          className="text-[10px] font-mono text-white transform rotate-[-30deg] whitespace-nowrap"
          style={{ letterSpacing: '2em' }}
        >
          {watermarkText} {watermarkText} {watermarkText}
        </span>
      </div>
    </div>
  );
};

/**
 * SecurityBadge Component
 * Shows current security level in the UI
 */
export const SecurityBadge = ({ status }) => {
  const { level, message, color, bgColor } = status;
  
  return (
    <div className={cn(
      'flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border',
      bgColor,
      color,
      level === 'warning' && 'border-amber-500/30',
      level === 'protected' && 'border-blue-500/30',
      level === 'secure' && 'border-emerald-500/30'
    )}>
      <Shield className="w-3 h-3" />
      <span>{message}</span>
    </div>
  );
};
